import axios from 'axios';

// Use environment variable with fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api'; 

// Add this function to consistently get the admin token
const getAdminToken = () => {
  const adminUser = localStorage.getItem('adminUser');
  if (adminUser) {
    try {
      const parsed = JSON.parse(adminUser);
      return parsed.token;
    } catch (e) {
      console.error('Error parsing admin user from localStorage:', e);
      return null;
    }
  }
  return null;
};

// Extend axios with our custom methods
declare module 'axios' {
  interface AxiosInstance {
    updateTransactionZipUrl(transactionId: number, zipUrl: string | null, error?: boolean): Promise<any>;
  }
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Enable sending cookies with cross-origin requests
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // First try to get device ID for device-based endpoints
    const deviceId = localStorage.getItem('deviceId');
    if (deviceId) {
      config.headers['device-id'] = deviceId;
    }
    
    // For admin endpoints, add the admin token
    const adminToken = getAdminToken();
    if (adminToken) {
      config.headers['Authorization'] = `Bearer ${adminToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Alternative admin API without credentials
// Create a separate instance for admin endpoints that doesn't use credentials
// to work around CORS issues in certain environments
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false // Don't send credentials
});

// Add token to adminApi requests
adminApi.interceptors.request.use(
  (config) => {
    // For admin endpoints, add the admin token in headers
    const adminToken = getAdminToken();
    if (adminToken) {
      config.headers['Authorization'] = `Bearer ${adminToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth endpoints
export const registerUser = async (email: string) => {
  // No need to get deviceId here, the interceptor adds it
  const response = await api.post('/auth/register', { email });
  return response.data;
};

// Admin endpoints - using adminApi as fallback if normal API fails
export const adminLogin = async (email: string, password: string) => {
  try {
    const response = await api.post('/admin/login', { email, password });
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.post('/admin/login', { email, password });
      return response.data;
    }
    throw error;
  }
};

export const getAdminVenues = async () => {
  try {
    const response = await api.get('/admin/venues');
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.get('/admin/venues');
      return response.data;
    }
    throw error;
  }
};

export const assignAdminToVenue = async (adminId: string, venueId: string) => {
  try {
    const response = await api.post('/admin/venues/assign', { adminId, venueId });
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.post('/admin/venues/assign', { adminId, venueId });
      return response.data;
    }
    throw error;
  }
};

export const createAdmin = async (email: string, password: string, venueId: string) => {
  try {
    const response = await api.post('/admin/create', { email, password, venueId });
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.post('/admin/create', { email, password, venueId });
      return response.data;
    }
    throw error;
  }
};

// New admin management API functions
export const getAllAdmins = async () => {
  try {
    const response = await api.get('/admin/all');
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.get('/admin/all');
      return response.data;
    }
    throw error;
  }
};

export const deleteAdmin = async (adminId: string) => {
  try {
    const response = await api.delete(`/admin/${adminId}`);
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.delete(`/admin/${adminId}`);
      return response.data;
    }
    throw error;
  }
};

// Admin verification endpoint
export const verifyAdmin = async (token?: string) => {
  // Prepare token config for all requests
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  
  try {
    // First try with credentials
    console.log('Attempting admin verification with credentials');
    const response = await api.get('/admin/verify', config);
    console.log('Admin verification successful');
    return response.data;
  } catch (error: any) {
    console.warn('Admin verification with credentials failed:', error.message);
    
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      
      try {
        // Try with adminApi (no credentials)
        const response = await adminApi.get('/admin/verify', config);
        console.log('Admin verification successful without credentials');
        return response.data;
      } catch (fallbackError: any) {
        console.error('Admin verification failed with both approaches:', fallbackError.message);
        
        // Try a third approach - direct fetch with no-cors mode as a last resort
        try {
          console.warn('Trying with fetch API and no-cors mode');
          // Note: This won't actually return useful data due to CORS restrictions
          // but it might help diagnose the issue
          const fetchResponse = await fetch(`${API_BASE_URL}/admin/verify`, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'omit',
            mode: 'no-cors'
          });
          console.log('Fetch response:', fetchResponse.type);
          // We can't read the response body due to CORS, so we'll throw an error
          throw new Error('Admin verification failed with all approaches');
        } catch (fetchError) {
          console.error('Fetch attempt also failed:', fetchError);
          throw fetchError;
        }
      }
    }
    
    throw error;
  }
};

// Device endpoints
export const registerDevice = async (deviceId: string, venueId?: string) => {
  const response = await api.post('/device/register', { deviceId, venueId });
  return response.data;
};

export const getAllDevices = async () => {
  const response = await api.get('/device/all');
  return response.data;
};

export const updateDeviceAuth = async (deviceId: string, isAuthorized: boolean) => {
  const response = await api.put('/device/authorize', { deviceId, isAuthorized });
  return response.data;
};

export const deleteDevice = async (deviceId: string) => {
  const response = await api.delete(`/device/${deviceId}`);
  return response.data;
};

export const getDeviceById = async (deviceId: string) => {
  const response = await api.get(`/device/${deviceId}`);
  return response.data;
};

// Transaction endpoints
export const createTransaction = async (transactionData: {
  email: string;
  videoIds: string[];
  totalAmount: number;
  venueId?: number | null;
  fieldId?: number | null;
  zipUrl?: string;
  promoId?: number;
  promoCode?: string;
  isStaging?: boolean;
}) => {
  const response = await api.post('/transaction', transactionData);
  return response.data;
};

export const getTransactions = async (filters?: { 
  venueId?: string | number, 
  fieldId?: string | number,
  page?: number,
  limit?: number,
  export?: boolean,
  email?: string,
  startDate?: string,
  endDate?: string
}) => {
  let endpoint = '/transaction';
  const params = new URLSearchParams();

  if (filters) {
    if (filters.venueId && filters.venueId !== 'all') {
      params.append('venueId', filters.venueId.toString());
    }
    
    if (filters.fieldId && filters.fieldId !== 'all') {
      params.append('fieldId', filters.fieldId.toString());
    }
    
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
    
    if (filters.export) {
      params.append('export', 'true');
    }
    
    if (filters.email) {
      params.append('email', filters.email);
    }
    
    if (filters.startDate) {
      params.append('startDate', filters.startDate);
    }
    
    if (filters.endDate) {
      params.append('endDate', filters.endDate);
    }
  }
  
  const queryString = params.toString();
  if (queryString) {
    endpoint += `?${queryString}`;
  }
  
  console.log('Calling API endpoint:', endpoint);
  const response = await api.get(endpoint);
  return response.data;
};

// New function to update transaction with zip URL
export const updateTransactionZipUrl = async (transactionId: number, zipUrl: string | null, error?: boolean) => {
  const response = await api.put(`/transaction/${transactionId}/zipUrl`, { zipUrl, error });
  return response.data;
};

// Video endpoints
export const uploadVideos = async (formData: FormData) => {
  // No need to get deviceId here, the interceptor adds it
  const response = await api.post('/video/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      // device-id will be added automatically by interceptor
    }
  });
  return response.data;
};

export const getVideos = async () => {
  const response = await api.get('/video');
  return response.data;
};

export const downloadVideo = async (videoId: string) => {
  const response = await api.get(`/video/download/${videoId}`);
  return response.data;
};

// Venue endpoints
export const getVenues = async () => {
  const response = await api.get('/venue');
  return response.data;
};

export const createVenue = async (venueData: { 
  name: string; 
  location: string; 
}) => {
  const response = await api.post('/venue/create', {
    ...venueData,
    deviceId: null
  });
  return response.data;
};

export const updateVenue = async (id: string, venueData: {
  name?: string;
  location?: string;
}) => {
  const response = await api.put(`/venue/${id}`, venueData);
  return response.data;
};

export const deleteVenue = async (id: string) => {
  const response = await api.delete(`/venue/${id}`);
  return response.data;
};

// Configuration endpoints
export const getConfigurations = async () => {
  try {
    const response = await api.get('/admin/config');
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.get('/admin/config');
      return response.data;
    }
    throw error;
  }
};

export const updateConfiguration = async (key: string, value: string) => {
  try {
    const response = await api.put('/admin/config', { key, value });
    return response.data;
  } catch (error: any) {
    if (error.code === 'ERR_NETWORK') {
      console.warn('Network error with credentials, trying without credentials');
      const response = await adminApi.put('/admin/config', { key, value });
      return response.data;
    }
    throw error;
  }
};

// Get a specific configuration by key
export const getConfiguration = async (key: string, defaultValue: string = ''): Promise<string> => {
  try {
    const configs = await getConfigurations();
    const config = configs.find((c: any) => c.key === key);
    return config ? config.value : defaultValue;
  } catch (error) {
    console.error(`Error fetching configuration for ${key}:`, error);
    return defaultValue;
  }
};

// Field endpoints
export const getAllFields = async () => {
  const response = await api.get('/field');
  return response.data;
};

export const getFieldsByVenue = async (venueId: string) => {
  const response = await api.get(`/field/venue/${venueId}`);
  return response.data;
};

export const createField = async (name: string, venueId: string) => {
  const response = await api.post('/field/create', { name, venueId });
  return response.data;
};

export const updateDeviceField = async (deviceId: string, fieldId: number | null) => {
  const response = await api.put('/field/device', { deviceId, fieldId });
  return response.data;
};

export const updateField = async (id: string, name: string) => {
  const response = await api.put(`/field/${id}`, { name });
  return response.data;
};

export const deleteField = async (id: string) => {
  const response = await api.delete(`/field/${id}`);
  return response.data;
};

// Promo code endpoints
export const getAllPromos = async () => {
  const response = await api.get('/promo');
  return response.data;
};

export const createPromo = async (promoData: {
  name: string;
  code: string;
  amount?: number;
  percent?: number;
  quota?: number;
}) => {
  const response = await api.post('/promo', promoData);
  return response.data;
};

export const updatePromo = async (
  id: string,
  promoData: {
    name?: string;
    code?: string;
    amount?: number;
    percent?: number;
    quota?: number;
  }
) => {
  const response = await api.put(`/promo/${id}`, promoData);
  return response.data;
};

export const deletePromo = async (id: string) => {
  const response = await api.delete(`/promo/${id}`);
  return response.data;
};

export const validatePromo = async (code: string) => {
  const response = await api.post('/promo/validate', { code });
  return response.data;
};

// Attach custom method to the api instance
api.updateTransactionZipUrl = async (transactionId: number, zipUrl: string | null, error?: boolean) => {
  const response = await api.put(`/transaction/${transactionId}/zipUrl`, { zipUrl, error });
  return response.data;
};

// Add function to get Google Drive storage quota
export const getGoogleDriveStorageQuota = async () => {
  const response = await api.get('/google-drive/storage-quota');
  return response.data;
};

// Add function to empty Google Drive
export const emptyGoogleDrive = async () => {
  const response = await api.post('/google-drive/empty-drive');
  return response.data;
};

// Add function to delete old files from Google Drive
export const deleteOldGoogleDriveFiles = async (olderThan: string) => {
  const response = await api.post('/google-drive/delete-old-files', { olderThan });
  return response.data;
};

export default api;